import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import * as api from '../../services/api';
import { UserSelectors } from '../../reducers/user';
import CreateTrainingModal from './CreateTrainingModal';

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

// ─── Page shell ──────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 32px;
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeIn} 0.22s ease;
`;

const Header = styled.div`
  margin-bottom: 36px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1A2744;
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #64748B;
  margin: 0;
`;

const SectionTitle = styled.h2`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #94A3B8;
  margin: 0 0 16px;
`;

// ─── Sport / bio cards ────────────────────────────────────────────────────────

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 40px;
`;

const Card = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  background: ${(p) => p.$active ? 'linear-gradient(135deg, #1E3A8A 0%, #0369A1 100%)' : '#fff'};
  border: 1.5px solid ${(p) => p.$active ? 'transparent' : '#E2EAF4'};
  border-radius: 14px;
  padding: 24px 20px;
  text-align: left;
  cursor: ${(p) => p.$disabled ? 'default' : 'pointer'};
  opacity: ${(p) => p.$disabled ? 0.55 : 1};
  box-shadow: ${(p) => p.$active ? '0 4px 18px rgba(3,105,161,0.22)' : '0 1px 4px rgba(0,0,0,0.06)'};
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: ${(p) => p.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${(p) => p.$disabled ? undefined : p.$active ? '0 8px 24px rgba(3,105,161,0.28)' : '0 4px 12px rgba(0,0,0,0.10)'};
  }
`;

const CardIcon = styled.div<{ $active?: boolean }>`
  font-size: 28px;
  margin-bottom: 12px;
  color: ${(p) => p.$active ? 'rgba(255,255,255,0.9)' : '#64748B'};
`;

const CardLabel = styled.div<{ $active?: boolean }>`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.$active ? '#fff' : '#1A2744'};
  margin-bottom: 4px;
`;

const CardDesc = styled.div<{ $active?: boolean }>`
  font-size: 12px;
  color: ${(p) => p.$active ? 'rgba(255,255,255,0.70)' : '#94A3B8'};
`;

const ComingSoonBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  background: #F1F5F9;
  color: #94A3B8;
  border-radius: 4px;
  padding: 2px 6px;
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// ─── Training session cards ───────────────────────────────────────────────────

const TrainingSectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const TrainingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 40px;
`;

const TrainingCard = styled.div`
  background: #fff;
  border: 1.5px solid #E2EAF4;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s;
  &:hover {
    border-color: #BAE6FD;
    box-shadow: 0 4px 18px rgba(3,105,161,0.13);
    transform: translateY(-2px);
  }
  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #0369A1, #059669);
    border-radius: 4px 0 0 4px;
  }
`;

const TrainingCardHead = styled.div`
  padding: 14px 16px 10px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const TrainingCardTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0F2042;
  line-height: 1.3;
`;

const TrainingCardDate = styled.div`
  font-size: 11.5px;
  color: #64748B;
  margin-top: 2px;
`;

const TrainingCardDeleteBtn = styled.button`
  flex-shrink: 0;
  width: 26px; height: 26px;
  border: none; border-radius: 7px;
  background: #FEF2F2; color: #EF4444;
  cursor: pointer; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #FEE2E2; }
`;

const TrainingExercises = styled.div`
  padding: 0 16px 10px 20px;
  font-size: 12px;
  color: #64748B;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TrainingStatsRow = styled.div`
  padding: 10px 16px 14px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  border-top: 1px solid #F1F5F9;
`;

const TrainingStatCol = styled.div`
  text-align: center;
`;

const TrainingStatLabel = styled.div`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #94A3B8;
  margin-bottom: 5px;
`;

const TrainingStatBlock = styled.div<{ $slot: 'pre' | 'post' | 'delta' }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$slot === 'pre' ? '#059669' : p.$slot === 'post' ? '#0369A1' : 'inherit'};
`;

const TrainingStatSlotLabel = styled.div<{ $slot: 'pre' | 'post' }>`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${p => p.$slot === 'pre' ? '#059669' : '#0369A1'};
  opacity: 0.8;
  margin-bottom: 2px;
`;

const Delta = styled.span<{ $positive: boolean | null }>`
  color: ${p => p.$positive === null ? '#94A3B8' : p.$positive ? '#16a34a' : '#dc2626'};
`;

const NewTrainingBtn = styled.button`
  padding: 8px 16px;
  font-size: 12.5px;
  font-weight: 700;
  border: 1.5px solid #0369A1;
  border-radius: 9px;
  background: #fff;
  color: #0369A1;
  cursor: pointer;
  &:hover { background: #EFF6FF; }
`;

const EmptyTraining = styled.div`
  background: #F8FAFC;
  border: 1.5px dashed #CBD5E1;
  border-radius: 14px;
  padding: 32px 20px;
  margin-bottom: 40px;
  text-align: center;
  color: #94A3B8;
  font-size: 13px;
`;

const DetailsBtn = styled.button`
  padding: 3px 9px; font-size: 10.5px; font-weight: 700; border-radius: 5px;
  border: 1.5px solid #E2EAF4; background: #F8FAFC; color: #64748B; cursor: pointer;
  white-space: nowrap;
  &:hover { border-color: #0369A1; color: #0369A1; background: #EFF6FF; }
`;

// ─── Detail modal ─────────────────────────────────────────────────────────────

const DetailOverlay = styled.div`
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(10,18,40,0.68);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
`;

const DetailBox = styled.div`
  background: #fff; border-radius: 18px;
  width: 92vw; max-width: 780px; max-height: 88vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22);
  animation: ${fadeIn} 0.18s ease;
`;

const DetailHead = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #E2EAF4;
  display: flex; align-items: center; justify-content: space-between;
`;

const DetailTitle = styled.div`
  font-size: 16px; font-weight: 800; color: #0F2042;
`;

const DetailSubtitle = styled.div`
  font-size: 12px; color: #64748B; margin-top: 2px;
`;

const DetailCloseBtn = styled.button`
  width: 30px; height: 30px; border-radius: 8px;
  border: none; background: #F1F5F9; cursor: pointer; font-size: 15px;
  display: flex; align-items: center; justify-content: center; color: #64748B;
  &:hover { background: #E2EAF4; }
`;

const DetailBody = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 28px;
  &::before {
    content: '';
    position: absolute;
    left: 10px; top: 12px; bottom: 12px;
    width: 2px; background: #E2EAF4; border-radius: 1px;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 24px;
  &:last-child { margin-bottom: 0; }
`;

const TimelineDot = styled.div<{ $color: string }>`
  position: absolute;
  left: -22px; top: 3px;
  width: 12px; height: 12px; border-radius: 50%;
  background: ${p => p.$color};
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px ${p => p.$color}33;
`;

const TimelineLabel = styled.div`
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: #94A3B8; margin-bottom: 6px;
`;

const TimelineCard = styled.div<{ $color: string }>`
  background: ${p => p.$color}0D;
  border: 1.5px solid ${p => p.$color}33;
  border-radius: 10px; padding: 12px 14px;
`;

const StatsGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 10px;
`;

const StatCell = styled.div`
  text-align: center; padding: 8px; background: rgba(255,255,255,0.7);
  border-radius: 8px; border: 1px solid #E2EAF4;
`;

const StatCellLabel = styled.div`
  font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: #94A3B8; margin-bottom: 4px;
`;

const StatCellValue = styled.div<{ $color?: string }>`
  font-size: 14px; font-weight: 800;
  color: ${p => p.$color || '#0F2042'};
`;

const DeltaGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  margin-top: 14px; padding-top: 14px; border-top: 1px dashed #E2EAF4;
`;

const DeltaCell = styled.div`
  text-align: center;
`;

const DeltaLabel = styled.div`
  font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: #94A3B8; margin-bottom: 3px;
`;

const DeltaValue = styled.div<{ $positive: boolean | null }>`
  font-size: 13px; font-weight: 800;
  color: ${p => p.$positive === null ? '#94A3B8' : p.$positive ? '#16a34a' : '#dc2626'};
`;

const ExercisesBox = styled.div`
  background: #F0F9FF; border-radius: 10px; padding: 12px 14px;
  font-size: 12.5px; color: #0C4A6E; line-height: 1.7; white-space: pre-wrap;
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface SkSession {
  id: string;
  session_date: string;
  source_filename: string;
  shot_count: number;
}

interface SessionStats {
  avg_carry: number | null;
  avg_ball_speed: number | null;
  avg_smash: number | null;
  avg_club_speed: number | null;
}

interface HwExercise {
  id: string;
  name: string;
  category: string;
}

interface NeuroTrainingSession {
  id: string;
  title: string;
  exercises: string;
  hw_exercises: HwExercise[];
  start_date: string;
  end_date: string;
  pre_session_ids: string[];
  post_session_ids: string[];
  pre_stats: SessionStats;
  post_stats: SessionStats;
  patient_account_id?: string;
  patient_name?: string;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) => {
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

// ─── Component ────────────────────────────────────────────────────────────────

const NeuroIQHub: React.FC = () => {
  const navigate = useNavigate();
  const isPatientSession = useSelector(UserSelectors().isPatientSession);
  const [skSessions, setSkSessions] = useState<SkSession[]>([]);
  const [trainingSessions, setTrainingSessions] = useState<NeuroTrainingSession[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<NeuroTrainingSession | null>(null);
  const [editingExercises, setEditingExercises] = useState(false);
  const [allExercises, setAllExercises] = useState<HwExercise[]>([]);
  const [editExIds, setEditExIds] = useState<Set<string>>(new Set());
  const [savingExercises, setSavingExercises] = useState(false);

  const refreshTrainingSessions = useCallback(async () => {
    try {
      const tRes = await (api as any).listNeuroTrainingSessions();
      if (tRes?.success) setTrainingSessions(tRes.sessions ?? []);
    } catch { /* ok */ }
  }, []);

  const loadData = useCallback(async () => {
    // Call independently so one failure doesn't block the other
    (api as any).getSkyTrakSessions()
      .then((r: any) => { if (r?.success) setSkSessions(r.sessions ?? []); })
      .catch(() => {});
    refreshTrainingSessions();
  }, [refreshTrainingSessions]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteTraining = async (id: string) => {
    if (!window.confirm('Delete this training session? This cannot be undone.')) return;
    await (api as any).deleteNeuroTrainingSession(id);
    setTrainingSessions(prev => prev.filter(t => t.id !== id));
  };

  const openEditExercises = async (session: NeuroTrainingSession) => {
    setEditExIds(new Set(session.hw_exercises.map(e => e.id)));
    setEditingExercises(true);
    if (allExercises.length === 0) {
      try {
        const exs = await api.getHomeworkExercises();
        setAllExercises(Array.isArray(exs) ? exs : []);
      } catch { /* ok */ }
    }
  };

  const saveExercises = async () => {
    if (!selectedSession) return;
    setSavingExercises(true);
    try {
      const res = await api.setNeuroTrainingSessionExercises(selectedSession.id, Array.from(editExIds));
      const updated = { ...selectedSession, hw_exercises: res.hw_exercises };
      setSelectedSession(updated);
      setTrainingSessions(prev => prev.map(t => t.id === updated.id ? updated : t));
      setEditingExercises(false);
    } catch { /* ok */ }
    finally { setSavingExercises(false); }
  };

  return (
    <Page>
      <Header>
        <Title>NeuroIQ</Title>
        <Subtitle>Sport Performance + Health Intelligence</Subtitle>
      </Header>

      {/* ── Sports Profile ── */}
      <SectionTitle>Sports Profile</SectionTitle>
      <CardGrid>
        <Card $active onClick={() => navigate('/neuroiq/golf')}>
          <CardIcon $active>⛳</CardIcon>
          <CardLabel $active>Golf</CardLabel>
          <CardDesc $active>SkyTrak shot metrics, dispersion, club performance</CardDesc>
        </Card>
        <Card $disabled>
          <CardIcon>🏈</CardIcon>
          <CardLabel>Football</CardLabel>
          <CardDesc>Performance tracking</CardDesc>
          <br /><ComingSoonBadge>Coming soon</ComingSoonBadge>
        </Card>
        <Card $disabled>
          <CardIcon>⚾</CardIcon>
          <CardLabel>Baseball</CardLabel>
          <CardDesc>Performance tracking</CardDesc>
          <br /><ComingSoonBadge>Coming soon</ComingSoonBadge>
        </Card>
        <Card $disabled>
          <CardIcon>🏒</CardIcon>
          <CardLabel>Hockey</CardLabel>
          <CardDesc>Performance tracking</CardDesc>
          <br /><ComingSoonBadge>Coming soon</ComingSoonBadge>
        </Card>
        <Card $active onClick={() => navigate('/homework')}>
          <CardIcon $active>📋</CardIcon>
          <CardLabel $active>Homework</CardLabel>
          <CardDesc $active>Assigned exercises and training session uploads</CardDesc>
        </Card>
      </CardGrid>

      {/* ── Neuro Training Sessions ── */}
      <TrainingSectionHead>
        <SectionTitle style={{ margin: 0 }}>Neuro Training Sessions</SectionTitle>
        <NewTrainingBtn onClick={() => setShowCreateModal(true)}>
          + New Training Session
        </NewTrainingBtn>
      </TrainingSectionHead>

      {trainingSessions.length === 0 ? (
        <EmptyTraining>
          {isPatientSession
            ? 'No training sessions yet. Your clinician will create sessions to track your progress.'
            : 'No training sessions yet. Build one to compare pre vs post-training performance across any sport.'}
        </EmptyTraining>
      ) : (
        <TrainingGrid>
          {trainingSessions.map(t => {
            const pre = t.pre_stats;
            const post = t.post_stats;
            const carryDelta = pre.avg_carry != null && post.avg_carry != null
              ? post.avg_carry - pre.avg_carry : null;
            const speedDelta = pre.avg_ball_speed != null && post.avg_ball_speed != null
              ? post.avg_ball_speed - pre.avg_ball_speed : null;
            const smashDelta = pre.avg_smash != null && post.avg_smash != null
              ? post.avg_smash - pre.avg_smash : null;
            const dateLabel = t.start_date === t.end_date
              ? fmtDate(t.start_date)
              : `${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}`;

            return (
              <TrainingCard key={t.id} onClick={() => setSelectedSession(t)}>
                <TrainingCardHead>
                  <div>
                    <TrainingCardTitle>{t.title || 'Neuro Training Session'}</TrainingCardTitle>
                    <TrainingCardDate>{dateLabel}</TrainingCardDate>
                    {!isPatientSession && t.patient_name && (
                      <div style={{ fontSize: 11, color: '#0369A1', fontWeight: 600, marginTop: 2 }}>
                        {t.patient_name}
                      </div>
                    )}
                    {!isPatientSession && !t.patient_name && t.patient_account_id && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                        {String(t.patient_account_id).slice(0, 8)}…
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <DetailsBtn onClick={e => { e.stopPropagation(); setSelectedSession(t); }}>⤢ Details</DetailsBtn>
                    <TrainingCardDeleteBtn title="Delete" onClick={e => { e.stopPropagation(); handleDeleteTraining(t.id); }}>×</TrainingCardDeleteBtn>
                  </div>
                </TrainingCardHead>

                {t.hw_exercises?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '6px 0' }}>
                    {t.hw_exercises.map((e: any) => (
                      <span key={e.id} style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                        background: '#EDE9FE', color: '#5B21B6',
                      }}>{e.name}</span>
                    ))}
                  </div>
                )}

                <TrainingStatsRow>
                  <TrainingStatCol>
                    <TrainingStatLabel>Avg Carry</TrainingStatLabel>
                    <TrainingStatSlotLabel $slot="pre">Pre</TrainingStatSlotLabel>
                    <TrainingStatBlock $slot="pre">
                      {pre.avg_carry != null ? `${pre.avg_carry.toFixed(0)} yd` : '—'}
                    </TrainingStatBlock>
                    <TrainingStatSlotLabel $slot="post" style={{ marginTop: 4 }}>Post</TrainingStatSlotLabel>
                    <TrainingStatBlock $slot="post">
                      {post.avg_carry != null ? `${post.avg_carry.toFixed(0)} yd` : '—'}
                    </TrainingStatBlock>
                    {carryDelta != null && (
                      <Delta $positive={carryDelta > 0} style={{ fontSize: 11, fontWeight: 700, marginTop: 4, display: 'block' }}>
                        {carryDelta > 0 ? '+' : ''}{carryDelta.toFixed(0)} yd
                      </Delta>
                    )}
                  </TrainingStatCol>

                  <TrainingStatCol>
                    <TrainingStatLabel>Ball Spd</TrainingStatLabel>
                    <TrainingStatSlotLabel $slot="pre">Pre</TrainingStatSlotLabel>
                    <TrainingStatBlock $slot="pre">
                      {pre.avg_ball_speed != null ? `${pre.avg_ball_speed.toFixed(0)} mph` : '—'}
                    </TrainingStatBlock>
                    <TrainingStatSlotLabel $slot="post" style={{ marginTop: 4 }}>Post</TrainingStatSlotLabel>
                    <TrainingStatBlock $slot="post">
                      {post.avg_ball_speed != null ? `${post.avg_ball_speed.toFixed(0)} mph` : '—'}
                    </TrainingStatBlock>
                    {speedDelta != null && (
                      <Delta $positive={speedDelta > 0} style={{ fontSize: 11, fontWeight: 700, marginTop: 4, display: 'block' }}>
                        {speedDelta > 0 ? '+' : ''}{speedDelta.toFixed(0)} mph
                      </Delta>
                    )}
                  </TrainingStatCol>

                  <TrainingStatCol>
                    <TrainingStatLabel>Smash</TrainingStatLabel>
                    <TrainingStatSlotLabel $slot="pre">Pre</TrainingStatSlotLabel>
                    <TrainingStatBlock $slot="pre">
                      {pre.avg_smash != null ? pre.avg_smash.toFixed(2) : '—'}
                    </TrainingStatBlock>
                    <TrainingStatSlotLabel $slot="post" style={{ marginTop: 4 }}>Post</TrainingStatSlotLabel>
                    <TrainingStatBlock $slot="post">
                      {post.avg_smash != null ? post.avg_smash.toFixed(2) : '—'}
                    </TrainingStatBlock>
                    {smashDelta != null && (
                      <Delta $positive={smashDelta > 0} style={{ fontSize: 11, fontWeight: 700, marginTop: 4, display: 'block' }}>
                        {smashDelta > 0 ? '+' : ''}{smashDelta.toFixed(2)}
                      </Delta>
                    )}
                  </TrainingStatCol>
                </TrainingStatsRow>
              </TrainingCard>
            );
          })}
        </TrainingGrid>
      )}

      {/* ── Bio Profile ── */}
      <SectionTitle>Bio Profile</SectionTitle>
      <CardGrid>
        <Card onClick={() => navigate('/modalities/biomarker')}>
          <CardIcon>🧪</CardIcon>
          <CardLabel>Biomarker Profile</CardLabel>
          <CardDesc>Blood panels, Vibrant Wellness, Rupa Health</CardDesc>
        </Card>
        <Card onClick={() => navigate('/modalities/symptoms')}>
          <CardIcon>📋</CardIcon>
          <CardLabel>Symptom Profile</CardLabel>
          <CardDesc>CHYPS questionnaire, symptom tracking</CardDesc>
        </Card>
        <Card onClick={() => navigate('/modalities/functional')}>
          <CardIcon>⚡</CardIcon>
          <CardLabel>Functional Profile</CardLabel>
          <CardDesc>Neckcare, VNG, functional modalities</CardDesc>
        </Card>
        <Card onClick={() => navigate('/modalities/imaging')}>
          <CardIcon>🔬</CardIcon>
          <CardLabel>Imaging Profile</CardLabel>
          <CardDesc>Imaging and scan results</CardDesc>
        </Card>
      </CardGrid>

      {selectedSession && (() => {
        const t = selectedSession;
        const pre = t.pre_stats;
        const post = t.post_stats;
        const preSessions = skSessions.filter(s => t.pre_session_ids.includes(s.id));
        const postSessions = skSessions.filter(s => t.post_session_ids.includes(s.id));
        const carryDelta = pre.avg_carry != null && post.avg_carry != null ? post.avg_carry - pre.avg_carry : null;
        const speedDelta = pre.avg_ball_speed != null && post.avg_ball_speed != null ? post.avg_ball_speed - pre.avg_ball_speed : null;
        const smashDelta = pre.avg_smash != null && post.avg_smash != null ? post.avg_smash - pre.avg_smash : null;
        const clubDelta = pre.avg_club_speed != null && post.avg_club_speed != null ? post.avg_club_speed - pre.avg_club_speed : null;
        const dateLabel = t.start_date === t.end_date
          ? fmtDate(t.start_date)
          : `${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}`;

        return (
          <DetailOverlay onClick={() => setSelectedSession(null)}>
            <DetailBox onClick={e => e.stopPropagation()}>
              <DetailHead>
                <div>
                  <DetailTitle>{t.title || 'Neuro Training Session'}</DetailTitle>
                  <DetailSubtitle>{dateLabel} · {t.pre_session_ids.length} pre / {t.post_session_ids.length} post</DetailSubtitle>
                </div>
                <DetailCloseBtn onClick={() => setSelectedSession(null)}>×</DetailCloseBtn>
              </DetailHead>
              <DetailBody>
                <Timeline>

                  {/* Pre Training */}
                  <TimelineItem>
                    <TimelineDot $color="#059669" />
                    <TimelineLabel>Pre-Training Sessions</TimelineLabel>
                    <TimelineCard $color="#059669">
                      {preSessions.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#94A3B8' }}>No sessions linked</div>
                      ) : preSessions.map(s => (
                        <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #05966918' }}>
                          <span style={{ fontWeight: 700, color: '#065F46' }}>{fmtDate(s.session_date)}</span>
                          <span style={{ color: '#6B7280', fontSize: 11, flex: 1, margin: '0 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.source_filename}</span>
                          <span style={{ fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>{s.shot_count} shots</span>
                        </div>
                      ))}
                      <StatsGrid style={{ marginTop: preSessions.length > 0 ? 12 : 0 }}>
                        {([
                          { label: 'Avg Carry', val: pre.avg_carry, unit: 'yd', dec: 0 },
                          { label: 'Ball Speed', val: pre.avg_ball_speed, unit: 'mph', dec: 0 },
                          { label: 'Smash', val: pre.avg_smash, unit: '', dec: 2 },
                          { label: 'Club Speed', val: pre.avg_club_speed, unit: 'mph', dec: 0 },
                        ] as {label:string;val:number|null;unit:string;dec:number}[]).map(m => (
                          <StatCell key={m.label}>
                            <StatCellLabel>{m.label}</StatCellLabel>
                            <StatCellValue $color="#059669">
                              {m.val != null ? `${m.val.toFixed(m.dec)}${m.unit ? ' '+m.unit : ''}` : '—'}
                            </StatCellValue>
                          </StatCell>
                        ))}
                      </StatsGrid>
                    </TimelineCard>
                  </TimelineItem>

                  {/* Training window */}
                  <TimelineItem>
                    <TimelineDot $color="#7C3AED" />
                    <TimelineLabel>Training Window</TimelineLabel>
                    <TimelineCard $color="#7C3AED">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#6D28D9' }}>{dateLabel}</div>
                        {!isPatientSession && (
                          <button
                            onClick={() => openEditExercises(t)}
                            style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, border: '1.5px solid #7C3AED', background: '#fff', color: '#7C3AED', cursor: 'pointer' }}
                          >
                            {t.hw_exercises?.length ? 'Edit Exercises' : '+ Assign Exercises'}
                          </button>
                        )}
                      </div>
                      {t.hw_exercises?.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {t.hw_exercises.map((e) => (
                            <span key={e.id} style={{
                              fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 12,
                              background: '#EDE9FE', color: '#5B21B6',
                            }}>{e.name}</span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#A78BFA' }}>
                          {isPatientSession ? 'No exercises assigned yet.' : 'Click "Assign Exercises" to link homework to this session.'}
                        </div>
                      )}
                    </TimelineCard>
                  </TimelineItem>

                  {/* Post Training */}
                  <TimelineItem>
                    <TimelineDot $color="#0369A1" />
                    <TimelineLabel>Post-Training Sessions</TimelineLabel>
                    <TimelineCard $color="#0369A1">
                      {postSessions.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#94A3B8' }}>No sessions linked</div>
                      ) : postSessions.map(s => (
                        <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #0369A118' }}>
                          <span style={{ fontWeight: 700, color: '#0C4A6E' }}>{fmtDate(s.session_date)}</span>
                          <span style={{ color: '#6B7280', fontSize: 11, flex: 1, margin: '0 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.source_filename}</span>
                          <span style={{ fontWeight: 700, color: '#0369A1', whiteSpace: 'nowrap' }}>{s.shot_count} shots</span>
                        </div>
                      ))}
                      <StatsGrid style={{ marginTop: postSessions.length > 0 ? 12 : 0 }}>
                        {([
                          { label: 'Avg Carry', val: post.avg_carry, unit: 'yd', dec: 0 },
                          { label: 'Ball Speed', val: post.avg_ball_speed, unit: 'mph', dec: 0 },
                          { label: 'Smash', val: post.avg_smash, unit: '', dec: 2 },
                          { label: 'Club Speed', val: post.avg_club_speed, unit: 'mph', dec: 0 },
                        ] as {label:string;val:number|null;unit:string;dec:number}[]).map(m => (
                          <StatCell key={m.label}>
                            <StatCellLabel>{m.label}</StatCellLabel>
                            <StatCellValue $color="#0369A1">
                              {m.val != null ? `${m.val.toFixed(m.dec)}${m.unit ? ' '+m.unit : ''}` : '—'}
                            </StatCellValue>
                          </StatCell>
                        ))}
                      </StatsGrid>
                    </TimelineCard>
                  </TimelineItem>

                  {/* Delta */}
                  <TimelineItem>
                    <TimelineDot $color="#F59E0B" />
                    <TimelineLabel>Performance Delta (Post − Pre)</TimelineLabel>
                    <TimelineCard $color="#F59E0B">
                      <DeltaGrid>
                        {([
                          { label: 'Avg Carry', delta: carryDelta, unit: 'yd', dec: 0 },
                          { label: 'Ball Speed', delta: speedDelta, unit: 'mph', dec: 0 },
                          { label: 'Smash', delta: smashDelta, unit: '', dec: 2 },
                          { label: 'Club Speed', delta: clubDelta, unit: 'mph', dec: 0 },
                        ] as {label:string;delta:number|null;unit:string;dec:number}[]).map(m => (
                          <DeltaCell key={m.label}>
                            <DeltaLabel>{m.label}</DeltaLabel>
                            <DeltaValue $positive={m.delta === null ? null : m.delta > 0}>
                              {m.delta === null
                                ? '—'
                                : `${m.delta > 0 ? '+' : ''}${m.delta.toFixed(m.dec)}${m.unit ? ' '+m.unit : ''}`}
                            </DeltaValue>
                          </DeltaCell>
                        ))}
                      </DeltaGrid>
                    </TimelineCard>
                  </TimelineItem>

                </Timeline>
              </DetailBody>
            </DetailBox>
          </DetailOverlay>
        );
      })()}

      {/* ── Exercise picker overlay (clinician only) ── */}
      {editingExercises && selectedSession && (
        <DetailOverlay onClick={() => setEditingExercises(false)}>
          <DetailBox onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <DetailHead>
              <div>
                <DetailTitle>Assign Exercises</DetailTitle>
                <DetailSubtitle>{selectedSession.title || 'Neuro Training Session'}</DetailSubtitle>
              </div>
              <DetailCloseBtn onClick={() => setEditingExercises(false)}>×</DetailCloseBtn>
            </DetailHead>
            <DetailBody style={{ maxHeight: 440, overflowY: 'auto' }}>
              {allExercises.length === 0 ? (
                <div style={{ color: '#94A3B8', fontSize: 13 }}>Loading exercises…</div>
              ) : (
                Object.entries(
                  allExercises.reduce<Record<string, HwExercise[]>>((acc, e) => {
                    (acc[e.category] = acc[e.category] || []).push(e);
                    return acc;
                  }, {})
                ).sort(([a], [b]) => a.localeCompare(b)).map(([cat, exs]) => (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', marginBottom: 6 }}>{cat}</div>
                    {exs.map(e => (
                      <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }}>
                        <input
                          type="checkbox"
                          checked={editExIds.has(e.id)}
                          onChange={() => setEditExIds(prev => {
                            const next = new Set(prev);
                            next.has(e.id) ? next.delete(e.id) : next.add(e.id);
                            return next;
                          })}
                        />
                        <span style={{ fontSize: 13, color: '#1A2744' }}>{e.name}</span>
                      </label>
                    ))}
                  </div>
                ))
              )}
            </DetailBody>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #E2EAF4', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setEditingExercises(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #E2EAF4', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancel</button>
              <button onClick={saveExercises} disabled={savingExercises} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#7C3AED,#5B21B6)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                {savingExercises ? 'Saving…' : 'Save'}
              </button>
            </div>
          </DetailBox>
        </DetailOverlay>
      )}

      {showCreateModal && (
        <CreateTrainingModal
          sessions={skSessions}
          isPatientSession={isPatientSession}
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => {
            setShowCreateModal(false);
            await refreshTrainingSessions();
          }}
        />
      )}
    </Page>
  );
};

export default NeuroIQHub;
